/**
 * WallAnalysisPage — análisis sísmico de muros (placas).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, Frame, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { SaveDesignDialog } from '@/features/designs/SaveDesignDialog';
import { useDesign } from '@/features/designs/useDesigns';
import {
  Card,
  Divider,
  ForceItem,
  NumberField,
  Row,
  RowKV,
  Section,
  SelectField,
  WarningsCard,
} from '@/features/analysis/shared/primitives';
import { useWallAnalysisStore } from '@/features/analysis/walls/store';
import { useWallAnalysisQuery } from '@/features/analysis/walls/useWallAnalysis';
import type { WallAnalysisRequest } from '@/features/analysis/walls/types';
import {
  SOIL_LABELS,
  SYSTEM_LABELS,
  USAGE_LABELS,
  USE_LABELS,
  ZONE_LABELS,
  type LiveLoadUse,
  type SoilProfile,
  type SystemKind,
  type UsageCategory,
  type ZoneCode,
} from '@/features/analysis/beams/types';

export function WallAnalysisPage() {
  const request = useWallAnalysisStore((s) => s.request);
  const setGeometry = useWallAnalysisStore((s) => s.setGeometry);
  const setAutoLoads = useWallAnalysisStore((s) => s.setAutoLoads);
  const updateFloor = useWallAnalysisStore((s) => s.updateFloor);
  const setSeismic = useWallAnalysisStore((s) => s.setSeismic);
  const setStiffness = useWallAnalysisStore((s) => s.setStiffness);
  const setMaterials = useWallAnalysisStore((s) => s.setMaterials);
  const loadRequest = useWallAnalysisStore((s) => s.loadRequest);

  const params = useParams<{ id?: string }>();
  const designId =
    params.id && params.id !== 'new' && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const designQuery = useDesign(designId);
  useEffect(() => {
    if (designQuery.data) {
      loadRequest(designQuery.data.payload as unknown as WallAnalysisRequest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designQuery.data?.id]);

  const { data, isFetching } = useWallAnalysisQuery(request);
  const navigate = useNavigate();
  const [saveOpen, setSaveOpen] = useState(false);

  const onDesign = () => {
    if (!data) return;
    navigate('/app/walls/new', {
      state: {
        prefill: {
          source: 'analysis',
          analysisId: designId,
          section: {
            lw: data.design_prefill.lw_cm,
            tw: data.design_prefill.tw_cm,
            hw: data.design_prefill.hw_cm,
            lbe: data.design_prefill.lbe_cm,
          },
          materials: { fc: request.fc, fy: request.fy, fyt: request.fy, cover: request.cover_cm },
          loads: {
            Pu: data.design_prefill.Pu_kgf,
            Mu: data.design_prefill.Mu_kgfcm,
            Vu: data.design_prefill.Vu_kgf,
          },
        },
      },
    });
  };

  return (
    <div className="flex h-full flex-col">
      <header
        className="flex h-14 shrink-0 items-center justify-between border-b px-6"
        style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
      >
        <div>
          <div
            className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]"
            style={{ color: 'var(--text-muted)' }}
          >
            <Frame size={12} strokeWidth={1.8} /> Análisis E.030 — muro estructural
          </div>
          <div className="text-sm font-semibold leading-none" style={{ color: 'var(--text-primary)' }}>
            Análisis de muros (placas)
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSaveOpen(true)}>
            <Save size={14} strokeWidth={1.8} /> {designId ? 'Actualizar' : 'Guardar'}
          </Button>
          <Button variant="primary" size="md" onClick={onDesign} disabled={!data || isFetching}>
            Diseñar muro <ArrowRight size={14} strokeWidth={2} />
          </Button>
        </div>
      </header>

      <SaveDesignDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        module="analysis_muros"
        payload={request as unknown as Record<string, unknown>}
        existing={designQuery.data ?? null}
      />

      <div className="grid flex-1 grid-cols-1 overflow-hidden lg:grid-cols-[340px_minmax(0,1fr)]">
        <div
          className="overflow-y-auto border-r p-5 scrollbar-thin"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-subtle)' }}
        >
          <Section title="Geometría">
            <Row>
              <NumberField label="lw (m)" value={request.geometry.lw_m} onChange={(v) => setGeometry({ lw_m: v })} step={0.1} />
              <NumberField label="tw (cm)" value={request.geometry.tw_cm} onChange={(v) => setGeometry({ tw_cm: v })} />
            </Row>
            <NumberField label="hw total (m)" value={request.geometry.hw_m} onChange={(v) => setGeometry({ hw_m: v })} step={0.5} />
          </Section>

          <Section title="Cargas gravitacionales">
            <NumberField
              label="A_trib por piso (m²)"
              value={request.auto_loads.tributary_area_m2}
              onChange={(v) => setAutoLoads({ tributary_area_m2: v })}
              step={0.5}
            />
            <NumberField
              label="DL losa (kgf/m²)"
              value={request.auto_loads.slab_dead_load_kgf_m2}
              onChange={(v) => setAutoLoads({ slab_dead_load_kgf_m2: v })}
              step={50}
            />
            <NumberField
              label="N° pisos"
              value={request.auto_loads.floors.length}
              onChange={(v) => {
                const n = Math.max(1, Math.min(20, Math.round(v)));
                const current = request.auto_loads.floors;
                if (n > current.length) {
                  for (let i = current.length; i < n; i++) {
                    setAutoLoads({
                      floors: [...current, { use: 'vivienda', height_m: 3.0 }],
                    });
                  }
                } else if (n < current.length) {
                  setAutoLoads({ floors: current.slice(0, n) });
                }
              }}
            />
            <SelectField
              label="Uso típico (todos los pisos)"
              value={request.auto_loads.floors[0]?.use ?? 'vivienda'}
              options={USE_LABELS as Record<string, string>}
              onChange={(v) => {
                // Aplicar a todos los pisos para simplicidad
                request.auto_loads.floors.forEach((_, i) => updateFloor(i, { use: v as LiveLoadUse }));
              }}
            />
          </Section>

          <Section title="Sismo">
            <SelectField
              label="Zona"
              value={request.seismic.zone}
              options={ZONE_LABELS as Record<string, string>}
              onChange={(v) => setSeismic({ zone: v as ZoneCode })}
            />
            <SelectField
              label="Categoría"
              value={request.seismic.usage}
              options={USAGE_LABELS as Record<string, string>}
              onChange={(v) => setSeismic({ usage: v as UsageCategory })}
            />
            <SelectField
              label="Suelo"
              value={request.seismic.soil}
              options={SOIL_LABELS as Record<string, string>}
              onChange={(v) => setSeismic({ soil: v as SoilProfile })}
            />
            <SelectField
              label="Sistema"
              value={request.seismic.system}
              options={SYSTEM_LABELS as Record<string, string>}
              onChange={(v) => setSeismic({ system: v as SystemKind })}
            />
            <NumberField
              label="Peso sísmico (kgf)"
              value={request.seismic.seismic_weight_kgf}
              onChange={(v) => setSeismic({ seismic_weight_kgf: v })}
              step={5000}
            />
            <NumberField
              label="Fracción V que toma este muro"
              value={request.wall_stiffness_ratio}
              onChange={(v) => setStiffness(v)}
              step={0.05}
            />
          </Section>

          <Section title="Materiales">
            <Row>
              <NumberField label="f'c" value={request.fc} onChange={(v) => setMaterials({ fc: v })} />
              <NumberField label="fy" value={request.fy} onChange={(v) => setMaterials({ fy: v })} />
            </Row>
          </Section>
        </div>

        <div className="overflow-y-auto p-6 scrollbar-thin">
          {data ? (
            <>
              <Card title="Cargas calculadas">
                <Row>
                  <div>
                    <RowKV k="P_D total" v={data.breakdown.P_D_total} unit="kgf" />
                    <RowKV k="P_L total" v={data.breakdown.P_L_total} unit="kgf" />
                    <RowKV k="PP muro" v={data.breakdown.PP_wall} unit="kgf" />
                    <Divider />
                    <RowKV k="Pu gravedad" v={data.breakdown.Pu_gravity} unit="kgf" bold accent />
                  </div>
                  <div>
                    <RowKV k="C" v={data.seismic.C} />
                    <RowKV k="T edif" v={data.seismic.T} unit="s" />
                    <RowKV k="V_basal edificio" v={data.seismic.V_basal_building} unit="kgf" />
                    <Divider />
                    <RowKV k="V_wall base" v={data.seismic.V_wall_base} unit="kgf" bold />
                    <RowKV k="Mu base" v={data.seismic.Mu_base / 100} unit="kgf·m" bold />
                    <RowKV k="Mu media altura" v={data.seismic.Mu_mid / 100} unit="kgf·m" />
                  </div>
                </Row>
              </Card>

              <Card title="Envolvente">
                <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-4">
                  <ForceItem label="Pu máx" value={data.envelope.Pu_max} unit="kgf" />
                  <ForceItem label="Pu mín" value={data.envelope.Pu_min} unit="kgf" />
                  <ForceItem label="Mu máx" value={data.envelope.Mu_max / 100} unit="kgf·m" />
                  <ForceItem label="Vu máx" value={data.envelope.Vu_max} unit="kgf" />
                </div>
                <div className="mt-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                  Combo gobernante: {data.envelope.governing_combo}
                  {' · '}lbe sugerido: {data.design_prefill.lbe_cm.toFixed(0)} cm
                </div>
              </Card>

              <WarningsCard items={data.warnings} />
            </>
          ) : (
            <div className="flex items-center justify-center py-20" style={{ color: 'var(--text-muted)' }}>
              <Loader2 size={20} className="mr-2 animate-spin" /> Calculando…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
